import { AppLayout } from "@/components/layout";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useAuth } from "@/hooks/use-auth";
import { apiFetch } from "@/lib/api";
import {
  Users, Mail, Calendar, Search, Send, Phone, LogIn,
  Crown, Video, MapPin, CreditCard, Package, Dumbbell,
  FileText, Lock, ChevronDown, ChevronUp, AlertTriangle, BellOff,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { useState, useEffect } from "react";
import { format } from "date-fns";
import { useLocation } from "wouter";

type Student = {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone: string | null;
  avatarUrl: string | null;
  createdAt: string;
  role: string;
  noShowAlertSuppressed?: boolean;
};

type MembershipStat = {
  planName: string;
  planPrivateCredits: number;
  privateCreditsRemaining: number;
  creditsUsed: number;
  creditsResetAt: string;
  currentPeriodStart: string | null;
};

type NoShowStat = {
  noShowCount: number;
  eligibleTotal: number;
  rate: number;
};

type SessionNote = {
  content: string;
  isInternal: boolean;
  authorName: string;
  authorRole: string;
  createdAt: string;
};

type SessionRecord = {
  id: string;
  status: string;
  proposedStart: string;
  proposedEnd: string;
  confirmedStart: string | null;
  durationMinutes: number;
  mode: string;
  paymentType: string;
  usedMembershipCredit: boolean;
  priceTotal: number;
  trainerName: string;
  notes: SessionNote[];
};

const STATUS_STYLES: Record<string, string> = {
  COMPLETED: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
  CONFIRMED: "bg-blue-500/10 text-blue-400 border-blue-500/20",
  PENDING: "bg-amber-500/10 text-amber-400 border-amber-500/20",
  CANCELLED: "bg-white/5 text-muted-foreground border-white/10",
  NO_SHOW: "bg-red-500/10 text-red-400 border-red-500/20",
};

function PaymentIcon({ type, usedMembership }: { type: string; usedMembership: boolean }) {
  if (usedMembership) return <Crown className="w-3.5 h-3.5 text-primary" />;
  if (type === "PACKAGE") return <Package className="w-3.5 h-3.5 text-purple-400" />;
  return <CreditCard className="w-3.5 h-3.5 text-muted-foreground" />;
}

function PaymentLabel({ type, usedMembership }: { type: string; usedMembership: boolean }) {
  if (usedMembership) return <span className="text-primary">Membership credit</span>;
  if (type === "PACKAGE") return <span className="text-purple-400">Package</span>;
  return <span className="text-muted-foreground">Pay-per-session</span>;
}

function SessionNotes({ notes }: { notes: SessionNote[] }) {
  const [expanded, setExpanded] = useState(false);
  if (notes.length === 0) return null;

  const preview = notes[0];
  const hasMore = notes.length > 1;

  return (
    <div className="mt-2 border-t border-white/5 pt-2">
      <button
        className="w-full text-left"
        onClick={e => { e.stopPropagation(); setExpanded(v => !v); }}
      >
        <div className="flex items-start gap-2 group">
          <FileText className="w-3 h-3 mt-0.5 shrink-0 text-muted-foreground group-hover:text-white transition-colors" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 mb-0.5">
              <span className="text-xs font-medium text-muted-foreground">
                {preview.authorName}
                {preview.authorRole === "STUDENT" && " (student)"}
              </span>
              {preview.isInternal && (
                <span className="flex items-center gap-0.5 text-[10px] text-amber-500/70">
                  <Lock className="w-2.5 h-2.5" /> internal
                </span>
              )}
              <span className="text-[10px] text-muted-foreground/50 ml-auto">
                {format(new Date(preview.createdAt), "MMM d")}
              </span>
              {hasMore && !expanded && (
                <ChevronDown className="w-3 h-3 text-muted-foreground" />
              )}
              {hasMore && expanded && (
                <ChevronUp className="w-3 h-3 text-muted-foreground" />
              )}
            </div>
            <p className={`text-xs text-white/70 leading-relaxed ${!expanded ? "line-clamp-2" : ""}`}>
              {preview.content}
            </p>
          </div>
        </div>
      </button>

      {expanded && notes.slice(1).map((note, i) => (
        <div key={i} className="flex items-start gap-2 mt-2 pt-2 border-t border-white/5">
          <FileText className="w-3 h-3 mt-0.5 shrink-0 text-muted-foreground" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 mb-0.5">
              <span className="text-xs font-medium text-muted-foreground">
                {note.authorName}
                {note.authorRole === "STUDENT" && " (student)"}
              </span>
              {note.isInternal && (
                <span className="flex items-center gap-0.5 text-[10px] text-amber-500/70">
                  <Lock className="w-2.5 h-2.5" /> internal
                </span>
              )}
              <span className="text-[10px] text-muted-foreground/50 ml-auto">
                {format(new Date(note.createdAt), "MMM d")}
              </span>
            </div>
            <p className="text-xs text-white/70 leading-relaxed">{note.content}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

function StudentDrawer({
  student,
  stats,
  open,
  onClose,
  onSuppressToggled,
}: {
  student: Student | null;
  stats: MembershipStat | null;
  open: boolean;
  onClose: () => void;
  onSuppressToggled?: (studentId: string, suppressed: boolean) => void;
}) {
  const [sessions, setSessions] = useState<SessionRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [suppressPending, setSuppressPending] = useState(false);
  const [localSuppressed, setLocalSuppressed] = useState(student?.noShowAlertSuppressed ?? false);

  useEffect(() => {
    setLocalSuppressed(student?.noShowAlertSuppressed ?? false);
  }, [student?.id, student?.noShowAlertSuppressed]);

  const handleToggleSuppress = async () => {
    if (!student) return;
    setSuppressPending(true);
    const next = !localSuppressed;
    try {
      await apiFetch(`/admin/students/${student.id}/no-show-alert-suppressed`, {
        method: "PATCH",
        body: JSON.stringify({ suppressed: next }),
      });
      setLocalSuppressed(next);
      onSuppressToggled?.(student.id, next);
    } catch {
      /* keep current state on error */
    } finally {
      setSuppressPending(false);
    }
  };

  useEffect(() => {
    if (!student || !open) return;
    setLoading(true);
    setSessions([]);
    apiFetch<SessionRecord[]>(`/admin/students/${student.id}/sessions`)
      .then(setSessions)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [student?.id, open]);

  const membershipSessions = sessions.filter(s => s.usedMembershipCredit).length;
  const noShowCount = sessions.filter(s => s.status === "NO_SHOW").length;
  const eligibleTotal = sessions.filter(s => s.status === "NO_SHOW" || s.status === "COMPLETED").length;
  const noShowRate = eligibleTotal > 0 ? Math.round((noShowCount / eligibleTotal) * 100) : 0;

  return (
    <Sheet open={open} onOpenChange={v => { if (!v) onClose(); }}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto bg-background border-white/10">
        {student && (
          <>
            <SheetHeader className="pb-4 border-b border-white/10">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-primary/20 flex items-center justify-center text-xl font-bold text-primary shrink-0">
                  {student.firstName[0]}{student.lastName[0]}
                </div>
                <div>
                  <SheetTitle className="text-white text-xl">
                    {student.firstName} {student.lastName}
                  </SheetTitle>
                  <p className="text-sm text-muted-foreground">{student.email}</p>
                  {student.phone && <p className="text-sm text-muted-foreground">{student.phone}</p>}
                </div>
              </div>

              {stats && (
                <div className="mt-4 p-3 rounded-lg bg-white/5 border border-white/10">
                  <div className="flex items-center justify-between mb-2">
                    <span className="flex items-center gap-1.5 text-sm font-medium text-primary">
                      <Crown className="w-3.5 h-3.5" /> {stats.planName}
                    </span>
                    <span className={`text-sm font-semibold ${stats.privateCreditsRemaining <= 1 ? "text-amber-400" : "text-white"}`}>
                      {stats.privateCreditsRemaining} / {stats.planPrivateCredits} remaining
                    </span>
                  </div>
                  <div className="w-full bg-white/5 h-1.5 rounded-full overflow-hidden mb-2">
                    <div
                      className={`h-full rounded-full ${stats.privateCreditsRemaining <= 1 ? "bg-amber-500" : "bg-primary/70"}`}
                      style={{
                        width: stats.planPrivateCredits > 0
                          ? `${Math.min(100, Math.round((stats.creditsUsed / stats.planPrivateCredits) * 100))}%`
                          : "0%",
                      }}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {stats.creditsUsed} used · resets {format(new Date(stats.creditsResetAt), "MMM d, yyyy")}
                  </p>
                </div>
              )}
            </SheetHeader>

            <div className="pt-4">
              {/* No-show rate stat + suppression toggle */}
              {!loading && eligibleTotal > 0 && (
                <div className={`rounded-xl border mb-4 overflow-hidden ${noShowCount > 0 ? "bg-orange-500/10 border-orange-500/30" : "bg-white/5 border-white/10"}`}>
                  <div className="flex items-center gap-3 p-3">
                    <AlertTriangle className={`w-4 h-4 shrink-0 ${noShowCount > 0 ? "text-orange-400" : "text-muted-foreground"}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span className={`text-xs font-medium ${noShowCount > 0 ? "text-orange-300" : "text-muted-foreground"}`}>
                          No-Show Rate
                        </span>
                        <span className={`text-sm font-bold font-mono ${noShowCount > 0 ? "text-orange-400" : "text-white"}`}>
                          {noShowRate}%
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {noShowCount} no-show{noShowCount !== 1 ? "s" : ""} out of {eligibleTotal} completed / no-show session{eligibleTotal !== 1 ? "s" : ""}
                      </p>
                    </div>
                  </div>
                  <div className="border-t border-white/10 px-3 py-2 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2 min-w-0">
                      <BellOff className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                      <span className="text-xs text-muted-foreground">
                        {localSuppressed ? "No-show alerts silenced for this student" : "Silence no-show alerts for this student"}
                      </span>
                    </div>
                    <button
                      onClick={handleToggleSuppress}
                      disabled={suppressPending}
                      className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none disabled:opacity-50 ${localSuppressed ? "bg-primary" : "bg-white/20"}`}
                      role="switch"
                      aria-checked={localSuppressed}
                    >
                      <span
                        className={`pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow-lg transition-transform ${localSuppressed ? "translate-x-4" : "translate-x-0"}`}
                      />
                    </button>
                  </div>
                </div>
              )}

              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-white flex items-center gap-2">
                  <Dumbbell className="w-4 h-4 text-muted-foreground" />
                  Session History
                </h3>
                {!loading && sessions.length > 0 && (
                  <div className="flex gap-3 text-xs text-muted-foreground">
                    <span>{sessions.length} total</span>
                    {membershipSessions > 0 && (
                      <span className="text-primary">{membershipSessions} membership</span>
                    )}
                  </div>
                )}
              </div>

              {loading ? (
                <div className="space-y-3">
                  {[1, 2, 3].map(i => (
                    <div key={i} className="h-20 bg-white/5 rounded-lg animate-pulse" />
                  ))}
                </div>
              ) : sessions.length === 0 ? (
                <div className="py-12 text-center">
                  <Dumbbell className="w-10 h-10 text-muted-foreground mx-auto mb-3 opacity-30" />
                  <p className="text-muted-foreground text-sm">No sessions yet</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {sessions.map(session => {
                    const start = new Date(session.proposedStart);
                    return (
                      <div
                        key={session.id}
                        className="p-3 rounded-lg bg-white/5 border border-white/5 hover:border-white/10 transition-colors"
                      >
                        <div className="flex items-start justify-between gap-2 mb-1.5">
                          <div>
                            <p className="text-sm font-medium text-white">
                              {format(start, "EEE, MMM d yyyy")}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {format(start, "h:mm a")} · {session.durationMinutes} min · {session.trainerName}
                            </p>
                          </div>
                          <div className="flex flex-col items-end gap-1">
                            <Badge
                              variant="outline"
                              className={`text-xs shrink-0 ${STATUS_STYLES[session.status] ?? "bg-white/5 text-muted-foreground border-white/10"}`}
                            >
                              {session.status.charAt(0) + session.status.slice(1).toLowerCase().replace("_", " ")}
                            </Badge>
                            {(session as any).lateCancellation && (
                              <Badge variant="outline" className="text-xs border-orange-500/40 bg-orange-500/10 text-orange-400">
                                Late Cancel
                              </Badge>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-3 mt-2 text-xs">
                          <span className="flex items-center gap-1 text-muted-foreground">
                            {session.mode === "VIRTUAL"
                              ? <Video className="w-3 h-3" />
                              : <MapPin className="w-3 h-3" />}
                            {session.mode === "VIRTUAL" ? "Virtual" : "In-person"}
                          </span>
                          <span className="flex items-center gap-1">
                            <PaymentIcon type={session.paymentType} usedMembership={session.usedMembershipCredit} />
                            <PaymentLabel type={session.paymentType} usedMembership={session.usedMembershipCredit} />
                          </span>
                          {session.priceTotal > 0 && !session.usedMembershipCredit && (
                            <span className="text-muted-foreground ml-auto">${session.priceTotal.toFixed(2)}</span>
                          )}
                        </div>
                        <SessionNotes notes={session.notes} />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

export default function AdminStudents() {
  const { user } = useAuth();
  const [students, setStudents] = useState<Student[]>([]);
  const [membershipStats, setMembershipStats] = useState<Record<string, MembershipStat>>({});
  const [noShowStats, setNoShowStats] = useState<Record<string, NoShowStat>>({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [drawerStudent, setDrawerStudent] = useState<Student | null>(null);
  const [, setLocation] = useLocation();

  useEffect(() => {
    if (!user?.gymId) return;
    Promise.all([
      apiFetch<Student[]>(`/admin/users/gym/${user.gymId}`),
      apiFetch<Record<string, MembershipStat>>(`/admin/membership-stats`),
      apiFetch<Record<string, NoShowStat>>(`/admin/student-no-show-stats`),
    ])
      .then(([usersData, statsData, noShowData]) => {
        setStudents(usersData.filter((u: Student) => u.role === "STUDENT"));
        setMembershipStats(statsData);
        setNoShowStats(noShowData);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [user?.gymId]);

  const filtered = students.filter(
    (s) =>
      `${s.firstName} ${s.lastName} ${s.email}`.toLowerCase().includes(search.toLowerCase())
  );

  const toggleStudent = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (filtered.every(s => selectedIds.has(s.id))) {
      setSelectedIds(prev => {
        const next = new Set(prev);
        filtered.forEach(s => next.delete(s.id));
        return next;
      });
    } else {
      setSelectedIds(prev => {
        const next = new Set(prev);
        filtered.forEach(s => next.add(s.id));
        return next;
      });
    }
  };

  const handleSendMessage = () => {
    const ids = Array.from(selectedIds).join(",");
    setLocation(`/admin/messaging?recipients=${ids}&role=STUDENT`);
  };

  async function impersonate(userId: string) {
    try {
      const result = await apiFetch<{ token?: string }>(`/admin/impersonate/${userId}`, { method: "POST" });
      if (result.token) {
        localStorage.setItem("originalToken", localStorage.getItem("token") || "");
        localStorage.setItem("impersonating", "true");
        localStorage.setItem("token", result.token);
        window.location.href = "/dashboard";
      }
    } catch (e: any) {
      console.error("Impersonate error", e);
    }
  }

  const allSelected = filtered.length > 0 && filtered.every(s => selectedIds.has(s.id));
  const membersCount = students.filter(s => membershipStats[s.id]).length;

  return (
    <AppLayout>
      <div className="flex flex-col gap-8 pb-12">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-4xl font-display font-bold text-white mb-2">Students</h1>
            <p className="text-muted-foreground">
              {students.length} registered · {membersCount} active member{membersCount !== 1 ? "s" : ""}
            </p>
          </div>
          {selectedIds.size > 0 && (
            <Button onClick={handleSendMessage} className="gap-2">
              <Send className="w-4 h-4" /> Send Message ({selectedIds.size})
            </Button>
          )}
        </div>

        <div className="flex items-center gap-3">
          {filtered.length > 0 && (
            <div className="flex items-center gap-2 shrink-0">
              <Checkbox checked={allSelected} onCheckedChange={toggleAll} />
              <span className="text-xs text-muted-foreground">All</span>
            </div>
          )}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search students..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 bg-background border-white/10"
            />
          </div>
        </div>

        {loading ? (
          <div className="space-y-4">{[1, 2, 3].map((i) => <div key={i} className="h-20 bg-white/5 rounded-xl animate-pulse" />)}</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center rounded-2xl border border-dashed border-white/10 bg-white/5">
            <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-50" />
            <p className="text-white font-medium text-lg">{search ? "No matching students" : "No students yet"}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map((student) => {
              const stats = membershipStats[student.id];
              const pct = stats && stats.planPrivateCredits > 0
                ? Math.min(100, Math.round((stats.privateCreditsRemaining / stats.planPrivateCredits) * 100))
                : 0;
              const daysUntilReset = stats
                ? Math.max(0, Math.ceil((new Date(stats.creditsResetAt).getTime() - Date.now()) / (1000 * 60 * 60 * 24)))
                : 0;
              const isLow = stats ? stats.privateCreditsRemaining <= 1 : false;

              return (
                <Card
                  key={student.id}
                  className={`glass-panel border-white/5 hover:border-white/10 transition-colors ${selectedIds.has(student.id) ? "ring-1 ring-primary border-primary/30" : ""}`}
                >
                  <CardContent className="p-5">
                    <div className="flex items-start gap-4">
                      <div className="flex items-center gap-3 shrink-0">
                        <Checkbox
                          checked={selectedIds.has(student.id)}
                          onCheckedChange={() => toggleStudent(student.id)}
                        />
                        <button
                          className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center text-lg font-bold text-primary shrink-0 hover:bg-primary/30 transition-colors"
                          onClick={() => setDrawerStudent(student)}
                          title="View session history"
                        >
                          {student.firstName[0]}{student.lastName[0]}
                        </button>
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-start justify-between gap-2">
                          <button
                            className="font-bold text-white text-lg truncate hover:text-primary transition-colors text-left"
                            onClick={() => setDrawerStudent(student)}
                          >
                            {student.firstName} {student.lastName}
                          </button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="shrink-0 h-7 w-7 text-muted-foreground hover:text-amber-400 hover:bg-amber-400/10"
                            title="View as this student"
                            onClick={() => impersonate(student.id)}
                          >
                            <LogIn className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                        <p className="text-sm text-muted-foreground flex items-center gap-1 truncate">
                          <Mail className="w-3.5 h-3.5 shrink-0" /> {student.email}
                        </p>
                        {student.phone && (
                          <p className="text-sm text-muted-foreground mt-1 flex items-center gap-1">
                            <Phone className="w-3.5 h-3.5 shrink-0" /> {student.phone}
                          </p>
                        )}
                        <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
                          <Calendar className="w-3 h-3" /> Joined {format(new Date(student.createdAt), "MMM d, yyyy")}
                        </p>

                        <div className="flex flex-wrap gap-1.5 mt-2">
                          {noShowStats[student.id]?.noShowCount > 0 && (
                            <div className="flex items-center gap-1 px-2 py-0.5 rounded-md bg-orange-500/10 border border-orange-500/20">
                              <AlertTriangle className="w-3 h-3 text-orange-400 shrink-0" />
                              <span className="text-xs text-orange-400 font-medium">
                                {noShowStats[student.id].noShowCount} no-show{noShowStats[student.id].noShowCount !== 1 ? "s" : ""} · {noShowStats[student.id].rate}%
                              </span>
                            </div>
                          )}
                          {student.noShowAlertSuppressed && (
                            <div className="flex items-center gap-1 px-2 py-0.5 rounded-md bg-white/5 border border-white/10">
                              <BellOff className="w-3 h-3 text-muted-foreground shrink-0" />
                              <span className="text-xs text-muted-foreground font-medium">Alerts silenced</span>
                            </div>
                          )}
                        </div>

                        {stats ? (
                          <div className="mt-3 pt-3 border-t border-white/5">
                            <div className="flex items-center justify-between mb-1.5">
                              <span className="flex items-center gap-1 text-xs text-primary font-medium">
                                <Crown className="w-3 h-3" /> {stats.planName}
                              </span>
                              <span className={`text-xs font-medium ${isLow ? "text-amber-400" : "text-muted-foreground"}`}>
                                {stats.creditsUsed} of {stats.planPrivateCredits} used
                              </span>
                            </div>
                            <div className="w-full bg-white/5 h-1.5 rounded-full overflow-hidden">
                              <div
                                className={`h-full rounded-full transition-all ${isLow ? "bg-amber-500" : "bg-primary/60"}`}
                                style={{ width: `${stats.planPrivateCredits > 0 ? 100 - pct : 0}%` }}
                              />
                            </div>
                            <p className="text-xs text-muted-foreground mt-1">
                              {stats.privateCreditsRemaining} remaining · resets in {daysUntilReset}d
                            </p>
                          </div>
                        ) : (
                          <div className="mt-3 pt-3 border-t border-white/5">
                            <p className="text-xs text-muted-foreground">No active membership</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      <StudentDrawer
        student={drawerStudent}
        stats={drawerStudent ? membershipStats[drawerStudent.id] ?? null : null}
        open={!!drawerStudent}
        onClose={() => setDrawerStudent(null)}
        onSuppressToggled={(studentId, suppressed) => {
          setStudents(prev => prev.map(s => s.id === studentId ? { ...s, noShowAlertSuppressed: suppressed } : s));
          setDrawerStudent(prev => prev?.id === studentId ? { ...prev, noShowAlertSuppressed: suppressed } : prev);
        }}
      />
    </AppLayout>
  );
}
